import { isEmpty } from '@ember/utils'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type { StudentsScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type { NormalizedInterval } from 'date-fns'

interface SummaryData {
  data: [{ category: 'average_score'; count: Nullable<number> }, { category: 'total_count'; count: number }]
}

interface MapData {
  map_number: number
  score: number
  created_at: number
}

interface ByGenreMapData {
  category: 'fiction' | 'nonfiction'
  map_number: number
  score: number
  created_at: number
  lexile_level: number
}

export interface AssessmentScoresStudentResponse {
  assessment_scores_events_summary: SummaryData
  assessment_scores_events: MapData[]
}

export interface AssessmentScoresStudentData {
  total_count: number
  average_score: Nullable<number>
  assessment_scores_events: {
    created_at: number
    map_number: number
    score: number
  }[]
}

export async function assessmentScoresStudent(
  this: StudentEventsService,
  product: LegacyProductRe | LegacyProductMs,
  statsScope: StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<AssessmentScoresStudentData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('assessment_scores_events', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = (await this.fetchData(url, query)) as AssessmentScoresStudentResponse
  const { assessment_scores_events_summary: summaryData, assessment_scores_events: mapData } = data
  if (mapData.length === 0) return null

  const sortedMapData = [...mapData].sort((a, b) => a.map_number - b.map_number)
  const averageScore = summaryData.data.find((value) => value.category === 'average_score')?.count ?? null
  const totalCount = summaryData.data.find((value) => value.category === 'total_count')?.count ?? 0

  return {
    average_score: averageScore,
    total_count: totalCount,
    assessment_scores_events: sortedMapData,
  }
}

export interface AssessmentScoresByGenreStudentResponse {
  assessment_scores_events_summary: SummaryData
  assessment_scores_events: ByGenreMapData[]
}

export interface AssessmentScoresByGenreStudentData {
  total_count: number
  average_score: Nullable<number>
  assessment_scores_events: {
    category: 'fiction' | 'nonfiction'
    created_at: number
    lexile_level: number
    map_number: number
    score: number
  }[]
}

export async function assessmentScoresByGenreStudent(
  this: StudentEventsService,
  product: LegacyProductRex,
  statsScope: StudentsScope,
  period: NormalizedInterval,
): Promise<Nullable<AssessmentScoresByGenreStudentData>> {
  if (isEmpty(statsScope.ids)) return null

  const url = this.buildUrl('assessment_scores_events', product, statsScope)
  const query = this.buildQuery(statsScope, period)

  const data = await this.fetchData<AssessmentScoresByGenreStudentResponse>(url, query)
  if (!data) return null
  const { assessment_scores_events_summary: summaryData, assessment_scores_events: mapData } = data
  if (mapData.length === 0) return null

  const sortedMapData = [...mapData].sort((a, b) => a.map_number - b.map_number)
  const averageScore = summaryData.data.find((value) => value.category === 'average_score')?.count ?? null
  const totalCount = summaryData.data.find((value) => value.category === 'total_count')?.count ?? 0

  return {
    average_score: averageScore,
    total_count: totalCount,
    assessment_scores_events: sortedMapData,
  }
}
